import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Button, Box, TextField,
  FormControl, FormLabel, Select, MenuItem, Alert, Slider,
  FormGroup, FormControlLabel, Checkbox, CircularProgress
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SurveyDefinition, SurveyQuestion } from '../types';
import { surveyApi } from '../utils/api';

interface MidSemesterResponses {
  reviewed_topics?: string;
  review_types?: string[];
  review_difficulties?: string[];
  other_difficulties?: string;
  review_helpfulness?: number;
  resources_used?: string;
}

export default function MidSemesterSurveyPage() {
  const navigate = useNavigate();
  const { data: definition, isLoading: loading } = useQuery<SurveyDefinition>({
    queryKey: ['survey', 'definition', 'midterm'],
    queryFn: () => surveyApi.getDefinition('midterm'),
  });
  const [responses, setResponses] = useState<MidSemesterResponses>({});
  const [error, setError] = useState('');

  const handleChange = (questionId: string, value: string | number | string[]) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultiselectChange = (questionId: string, optionValue: string, checked: boolean) => {
    setResponses(prev => {
      const current = (prev[questionId as keyof MidSemesterResponses] as string[]) || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, optionValue] };
      } else {
        return { ...prev, [questionId]: current.filter(v => v !== optionValue) };
      }
    });
  };

  const submitMutation = useMutation({
    mutationFn: (r: MidSemesterResponses) => surveyApi.submitMidSurvey(r),
    onSuccess: () => navigate('/assessment'),
    onError: () => setError('Failed to submit survey. Please try again.'),
  });

  const handleSubmit = () => {
    setError('');
    submitMutation.mutate(responses);
  };

  const renderQuestion = (question: SurveyQuestion, displayNumber: number) => {
    // Check if question should be conditionally displayed
    if (question.conditional) {
      const dependsOnValue = responses[question.conditional.dependsOn as keyof MidSemesterResponses];
      const shouldShow = Array.isArray(dependsOnValue) 
        ? dependsOnValue.includes(question.conditional.value)
        : dependsOnValue === question.conditional.value;
      
      if (!shouldShow) {
        return null;
      }
    }

    const isConditional = !!question.conditional;
    
    switch (question.type) {
      case 'scale':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
            <FormLabel id={`question-${question.id}-label`}>
              {!isConditional && `${displayNumber}. `}{question.question}
            </FormLabel>
            <Box sx={{ px: 2, pt: 2 }}>
              <Slider
                value={responses[question.id as keyof MidSemesterResponses] as number || question.min}
                onChange={(_, value) => handleChange(question.id, value as number)}
                min={question.min}
                max={question.max}
                step={1}
                marks={[
                  { value: question.min!, label: question.min_label },
                  { value: question.max!, label: question.max_label },
                ]}
                valueLabelDisplay="auto"
                aria-labelledby={`question-${question.id}-label`}
              />
            </Box>
          </FormControl>
        );

      case 'select':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto', ...(isConditional && { pl: 4 }) }}>
            <FormLabel id={`question-${question.id}-label`} sx={{ mb: 1, color: 'text.primary' }}>
              {!isConditional && `${displayNumber}. `}{question.question}
            </FormLabel>
            <Select
              labelId={`question-${question.id}-label`}
              value={responses[question.id as keyof MidSemesterResponses] || ''}
              onChange={(e) => handleChange(question.id, e.target.value)}
              aria-labelledby={`question-${question.id}-label`}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <em style={{ color: '#999' }}>Select an option</em>;
                }
                return question.options?.find(opt => opt.value === selected)?.label || selected;
              }}
            >
              <MenuItem value="" disabled>
                <em>Select an option</em>
              </MenuItem>
              {question.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'multiselect':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto', ...(isConditional && { pl: 4 }) }}>
            <FormLabel id={`question-${question.id}-label`} sx={{ mb: 1, color: 'text.primary' }}>
              {!isConditional && `${displayNumber}. `}{question.question}
            </FormLabel>
            <FormGroup>
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={((responses[question.id as keyof MidSemesterResponses] as string[]) || []).includes(option.value)}
                      onChange={(e) => handleMultiselectChange(question.id, option.value, e.target.checked)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </FormControl>
        );

      case 'textarea':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto', ...(isConditional && { pl: 4 }) }}>
            <FormLabel id={`question-${question.id}-label`} sx={{ mb: 1, color: 'text.primary' }}>
              {!isConditional && `${displayNumber}. `}{question.question}
            </FormLabel>
            <TextField
              value={responses[question.id as keyof MidSemesterResponses] || ''}
              onChange={(e) => handleChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              multiline
              rows={3}
              variant="outlined"
              inputProps={{
                'aria-labelledby': `question-${question.id}-label`,
              }}
            />
          </FormControl>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 3 }}>
        <Card sx={{ maxWidth: 800, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 3 }} />
            <Typography>Loading survey...</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!definition) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 3 }}>
        <Card sx={{ maxWidth: 800, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error">{error || 'Failed to load survey'}</Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4, px: 2 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Card>
          <CardContent sx={{ p: 8 }}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              {definition.title}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Estimated time: {definition.estimated_time}
            </Typography>

            <Typography variant="body2" paragraph sx={{ fontSize: '1rem', mb: 5 }}>
              {definition.instructions}
            </Typography>

            <Box component="form" noValidate>
              {definition.questions.map((question, index) => {
                // Calculate display number (skip conditional questions)
                const displayNumber = definition.questions
                  .slice(0, index)
                  .filter(q => !q.conditional)
                  .length + (question.conditional ? 0 : 1);
                return renderQuestion(question, displayNumber);
              })}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} role="alert">
                {error}
              </Alert>
            )}

            <Box textAlign="center" sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/assessment')}
                disabled={submitMutation.isPending}
                sx={{ px: 6, mr: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={submitMutation.isPending}
                sx={{ px: 6 }}
              >
                {submitMutation.isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
